import type { QueryClient } from '@tanstack/react-query';

import { HTTP_STATUS, isApiError } from '@brightlocal/api-client';
import type { LocationId } from '@brightlocal/shared';

import { apiClient } from 'shared/apiClient';
import { ENTITLEMENTS_STALE_TIME } from 'shared/consts';
import type { ErrorReport, ErrorReporting } from 'shared/utils';

import { CAPABILITIES_FEATURE_TAG, LOCATION_CAPABILITIES_QUERY_KEY } from './consts';

/** A location the user can't open or that doesn't exist; the `$id` route shows its error page for it. */
const isLocationErrorHandledByRoute = (error: unknown) =>
  isApiError(error) && (error.status === HTTP_STATUS.FORBIDDEN || error.status === HTTP_STATUS.NOT_FOUND);

/**
 * Query options for one location's capabilities. Each gated route with a location tool code loads
 * them through `revalidateLocationCapabilities`, so the gates read from the cache. Freshness and retries match the
 * customer capabilities. The loaders run alongside the `$id` location request, so a 403 or 404 is
 * not reported here: the location request already handles it.
 */
export const locationCapabilitiesQuery = (locationId: LocationId) => ({
  queryKey: [LOCATION_CAPABILITIES_QUERY_KEY, locationId] as const,
  queryFn: ({ signal }: { signal: AbortSignal }) =>
    apiClient.capabilities.getLocationCapabilities(locationId, { signal }),
  staleTime: ENTITLEMENTS_STALE_TIME,
  meta: {
    errorReporting: {
      feature: CAPABILITIES_FEATURE_TAG,
      errorMessage: 'Failed to fetch location capabilities',
      method: 'apiClient.capabilities.getLocationCapabilities',
      resolve: (error: unknown, defaultReport: ErrorReport | null) =>
        isLocationErrorHandledByRoute(error) ? null : defaultReport
    } satisfies ErrorReporting
  }
});

interface RevalidateLocationCapabilitiesParams {
  /** Router-context query client */
  queryClient: QueryClient;
  /** Location whose capabilities are read */
  locationId: LocationId;
}

/**
 * Returns a location's cached capabilities while they are fresh (younger than
 * {@link ENTITLEMENTS_STALE_TIME}) and refetches them otherwise, so a gate never decides from stale
 * access. Resolves to `null` when the read fails, so a guard fails closed instead of erroring; the
 * failure is reported through the query's `meta`.
 */
export const revalidateLocationCapabilities = ({ queryClient, locationId }: RevalidateLocationCapabilitiesParams) =>
  queryClient.fetchQuery(locationCapabilitiesQuery(locationId)).catch(() => null);
